import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export interface ModulePermissions {
  analytics: boolean;
  chat: boolean;
  messengers: boolean;
  tours: boolean;
  contacts: boolean;
  tasks: boolean;
  support: boolean;
  admin: boolean;
}

export const usePermissions = () => {
  const { role } = useAuth();
  const [permissions, setPermissions] = useState<ModulePermissions>({
    analytics: false,
    chat: false,
    messengers: false,
    tours: false,
    contacts: false,
    tasks: false,
    support: false,
    admin: false,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPermissions = async () => {
      if (!role) {
        setLoading(false);
        return;
      }

      // Для встроенных ролей admin имеет доступ ко всему
      if (role === 'admin') {
        setPermissions({
          analytics: true,
          chat: true,
          messengers: true,
          tours: true,
          contacts: true,
          tasks: true,
          support: true,
          admin: true,
        });
        setLoading(false);
        return;
      }

      // Руководитель имеет доступ ко всему кроме админ-панели
      if (role === 'manager') {
        setPermissions({
          analytics: true,
          chat: true,
          messengers: true,
          tours: true,
          contacts: true,
          tasks: true,
          support: true,
          admin: false,
        });
        setLoading(false);
        return;
      }

      // Для кастомных ролей загружаем разрешения из базы
      const { data: roleData } = await supabase
        .from('custom_roles')
        .select('permissions')
        .eq('id', role)
        .single();

      if (roleData?.permissions) {
        const perms = roleData.permissions as any;
        setPermissions({
          analytics: perms.analytics || false,
          chat: perms.chat || false,
          messengers: perms.messengers || false,
          tours: perms.tours || false,
          contacts: perms.contacts || false,
          tasks: perms.tasks || false,
          support: perms.support || false,
          admin: perms.admin || false,
        });
      } else {
        // Роль user по умолчанию имеет базовый доступ
        setPermissions({
          analytics: true,
          chat: true,
          messengers: true,
          tours: true,
          contacts: true,
          tasks: true,
          support: true,
          admin: false,
        });
      }

      setLoading(false);
    };

    loadPermissions();
  }, [role]);

  return { permissions, loading };
};
